import { z } from 'zod'

export const UserSchema = z.object({
  username: z.string(),
  email: z.string(),
  displayName: z.string(),
  displayNamePermanent: z.boolean(),
  dates: z.object({
    createdAt: z.string(),
    createdAtShort: z.string(),
    dateOfBirth: z.string(),
    dateOfBirthShort: z.string(),
    dateOfBirthNum: z.string(),
  }),
  bioText: z.string(),
  verified: z.boolean(),
  likedPosts: z.array(z.string()).nullable(),
  profilePicture: z.string().nullable(),
  headerPicture: z.string().nullable(),
  userFollowsCount: z.object({
    followingCount: z.number(),
    followersCount: z.number(),
    following: z.array(z.string()).nullable(),
    followers: z.array(z.string()).nullable(),
  }),
  postCount: z.number().nullable(),
  replyCount: z.number().nullable(),
  userInfoChangeCount: z.number().nullable(),
})

// change password min to 8 after wiping users db
const SignInUserSchema = UserSchema.pick({
  email: true,
}).extend({
  password: z.string().min(5),
})

const SignUpUserSchema = z.object({
  username: UserSchema.shape.username,
  email: UserSchema.shape.email,
  password: z.string().min(8),
  dateOfBirth: UserSchema.shape.dates.shape.dateOfBirthNum,
})

const UserUpdatesSchema = z.object({
  profilePicture: UserSchema.shape.profilePicture,
  headerPicture: UserSchema.shape.headerPicture,
  profilePicturePublicId: z.string().nullable(),
  headerPicturePublicId: z.string().nullable(),
  displayName: z.string().nullable(),
  bioText: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  email: UserSchema.shape.email,
  // profilePictureActive: z.boolean(),
  // headerPictureActive: z.boolean(),
})

const UserUpdatedSchema = z.object({
  profilePicture: UserSchema.shape.profilePicture,
  headerPicture: UserSchema.shape.headerPicture,
  displayName: UserSchema.shape.displayName,
  bioText: UserSchema.shape.bioText,
  dateOfBirth: UserSchema.shape.dates.shape.dateOfBirthNum,
})

const UserFollowSchema = UserSchema.pick({
  profilePicture: true,
  username: true,
  displayName: true,
  bioText: true,
})

export const UserFollowsSchema = z.object({
  following: z.array(UserFollowSchema),
  followers: z.array(UserFollowSchema),
})

const UserSimpleSchema = UserSchema.pick({
  profilePicture: true,
  username: true,
  displayName: true,
})

export const UsersSimpleSchema = z.object({
  users: z.array(UserSimpleSchema),
})

// aside from CurrentUser, other types here originates from the frontend and is being built during run time by the user and not supplied by the backend/api... so I'm wondering if we should be using zod for those...

export type CurrentUser = z.infer<typeof UserSchema>
export type SignInUser = z.infer<typeof SignInUserSchema>
export type SignUpUser = z.infer<typeof SignUpUserSchema>
export type UserUpdates = z.infer<typeof UserUpdatesSchema>
export type UserUpdated = z.infer<typeof UserUpdatedSchema>
export type UserFollow = z.infer<typeof UserFollowSchema>
export type UserFollows = z.infer<typeof UserFollowsSchema>
export type UserSimple = z.infer<typeof UserSimpleSchema>
export type UsersSimple = z.infer<typeof UsersSimpleSchema>

export const isCurrentUser = (user: any): user is CurrentUser => {
  return UserSchema.safeParse(user).success
}

export const isSignInUser = (user: any): user is SignInUser => {
  return SignInUserSchema.safeParse(user).success
}

export const isSignUpUser = (user: any): user is SignUpUser => {
  return SignUpUserSchema.safeParse(user).success
}
